import { extractErrorMessage } from "../utils/acceptSuggestions";
/**
 * ProcessOwnerRecoveryWizard - 7-Step Recovery Planning Wizard
 * ISO 22301 Clauses 8.3 (Recovery Strategy) and 8.4 (Recovery Plans)
 * Allows Process Owners to create comprehensive recovery packages
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { RecoveryTaskTable } from "../components/tables/RecoveryTaskTable";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  WizardContainer,
  WizardStepIndicator,
  WizardStep,
  WizardNavigation,
} from "../components/ui/wizard-components";
import {
  FileText,
  Server,
  Target,
  ClipboardList,
  GitBranch,
  Building2,
  CheckCircle2,
  RefreshCw,
  Cloud,
  Database,
  HardDrive,
  Globe,
  AlertTriangle,
  Clock,
  DollarSign,
  Users,
  Plus,
  Trash2,
  ArrowRight,
  ArrowLeft,
  Send,
  Save,
  Info,
  Zap,
  Snowflake,
  Timer,
  Wrench,
  ClipboardCheck,
} from "lucide-react";

// Wizard step configuration
const WIZARD_STEPS = [
  { id: 1, title: "BIA Summary", description: "Review your BIA data", icon: FileText, clause: "8.2.2" },
  { id: 2, title: "Technology", description: "Classify your technology", icon: Server, clause: "8.3" },
  { id: 3, title: "Strategy", description: "Select recovery strategy", icon: Target, clause: "8.3" },
  { id: 4, title: "Procedures", description: "Define recovery steps", icon: ClipboardList, clause: "8.4.4" },
  { id: 5, title: "Dependencies", description: "Map handoffs", icon: GitBranch, clause: "8.4.4" },
  { id: 6, title: "Infrastructure", description: "IT & Facility support", icon: Building2, clause: "8.4.4" },
  { id: 7, title: "Review", description: "Submit for approval", icon: CheckCircle2, clause: "8.4" },
];

// Hosting types
const HOSTING_TYPES = [
  { id: "public_cloud", name: "Public Cloud (AWS/Azure/GCP)", icon: Cloud, description: "You manage DevOps and application recovery" },
  { id: "private_cloud", name: "Private Cloud (Organization)", icon: Server, description: "IT Team supports infrastructure" },
  { id: "on_premise", name: "On-Premise Data Center", icon: HardDrive, description: "IT + Facility Teams support infrastructure" },
  { id: "hybrid", name: "Hybrid (Multiple locations)", icon: Database, description: "Specify for each component" },
  { id: "saas", name: "SaaS Application (Vendor)", icon: Globe, description: "Vendor responsible, you manage data/config" },
];

// Recovery strategies
const RECOVERY_STRATEGIES = [
  { 
    id: "hot_standby", 
    name: "Hot Standby", 
    rtoRange: "0-4 hours",
    icon: Zap,
    color: "bg-red-100 text-red-700 border-red-300",
    description: "Active-Active or Active-Passive. Immediate failover capability.",
    cost: "High",
    features: ["Real-time replication", "Automatic failover", "Minimal data loss"]
  },
  { 
    id: "warm_standby", 
    name: "Warm Standby", 
    rtoRange: "4-24 hours",
    icon: Timer,
    color: "bg-amber-100 text-amber-700 border-amber-300",
    description: "Pre-configured standby systems. Requires activation and data sync.",
    cost: "Medium",
    features: ["Regular backups", "Manual failover", "Some data loss acceptable"]
  },
  { 
    id: "cold_standby", 
    name: "Cold Standby", 
    rtoRange: "24-72 hours",
    icon: Snowflake,
    color: "bg-blue-100 text-blue-700 border-blue-300",
    description: "Rebuild from backups. Manual intervention required.",
    cost: "Low",
    features: ["Periodic backups", "Full rebuild required", "Longer recovery time"]
  },
  { 
    id: "manual_workaround", 
    name: "Manual Workaround", 
    rtoRange: "Varies",
    icon: Wrench,
    color: "bg-slate-100 text-slate-700 border-slate-300",
    description: "Business operates without technology. Paper-based or alternative processes.",
    cost: "Minimal",
    features: ["No technology dependency", "Business continuity focus", "Limited functionality"]
  },
];

export default function ProcessOwnerRecoveryWizard() {
  const { biaId, packageId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [existingPackageId, setExistingPackageId] = useState(packageId || null);
  
  // Approver selection state
  const [orgUsers, setOrgUsers] = useState([]);
  const [selectedApprover, setSelectedApprover] = useState(null);
  
  // BIA Summary (auto-populated)
  const [biaSummary, setBiaSummary] = useState(null);
  
  // Form data for each step
  const [formData, setFormData] = useState({
    // Step 2: Technology Classification
    technology_classification: {
      hosting_type: "",
      cloud_provider: "",
      devops_managed: false,
      infrastructure_team_required: false,
      facility_team_required: false,
      technology_components: [],
    },
    // Step 3: Recovery Strategy
    recovery_strategy: {
      strategy_type: "",
      target_rto_hours: 0,
      planned_rto_hours: 0,
      justification: "",
      cost_estimate: "medium",
      strategy_details: "",
    },
    // Step 4: Recovery Procedures
    business_recovery: {
      manual_workarounds: "",
      workaround_duration_hours: 8,
      minimum_staff_required: 1,
      alternate_location: "",
      critical_contacts: "",
    },
    application_recovery: {
      recovery_tasks: [],
      steps: [], // legacy compat
      failover_procedure: "",
      data_restoration_procedure: "",
      validation_checklist: [],
    },
    data_recovery: {
      backup_location: "",
      backup_frequency: "daily",
      backup_retention_days: 30,
      restore_procedure: "",
      estimated_restore_time_hours: 2,
    },
    // Step 5: Dependencies
    dependency_handoffs: {
      upstream: [],
      downstream: [],
    },
    // Step 6: Infrastructure
    infrastructure_support: {
      it_team_required: false,
      it_requirements: {
        servers: [],
        storage_gb: 0,
        network_requirements: "",
        special_requirements: "",
      },
      facility_team_required: false,
      facility_requirements: {
        location: "",
        power_requirements: "",
        physical_access: "",
        special_requirements: "",
      },
    },
  });

  // Fetch BIA data and existing package
  const fetchData = useCallback(async () => {
    if (!biaId && !packageId) {
      toast.error("No BIA or package specified");
      navigate("/operational-bia");
      return;
    }

    try {
      setLoading(true);
      
      if (packageId) {
        // Load existing package
        const pkgResponse = await api.get(`/recovery-packages/${packageId}`);
        const pkg = pkgResponse.data;
        
        setBiaSummary(pkg.bia_summary);
        setExistingPackageId(packageId);
        setCurrentStep(pkg.current_step || 1);
        
        // Load form data from package
        setFormData({
          technology_classification: pkg.technology_classification || formData.technology_classification,
          recovery_strategy: pkg.recovery_strategy || formData.recovery_strategy,
          business_recovery: pkg.business_recovery || formData.business_recovery,
          application_recovery: {
            ...formData.application_recovery,
            ...pkg.application_recovery,
            recovery_tasks: pkg.application_recovery?.recovery_tasks || [],
            steps: pkg.application_recovery?.steps || [],
          },
          data_recovery: pkg.data_recovery || formData.data_recovery,
          dependency_handoffs: pkg.dependency_handoffs || formData.dependency_handoffs,
          infrastructure_support: pkg.infrastructure_support || formData.infrastructure_support,
        });
      } else {
        // Load BIA for new package
        const biaResponse = await api.get(`/recovery-packages/bia/${biaId}/for-recovery`);
        setBiaSummary(biaResponse.data.bia_summary);
        
        if (biaResponse.data.existing_package_id) {
          setExistingPackageId(biaResponse.data.existing_package_id);
          toast.info("A recovery package already exists for this BIA. Loading...");
          // Fetch the existing package
          const pkgResponse = await api.get(`/recovery-packages/${biaResponse.data.existing_package_id}`);
          const pkg = pkgResponse.data;
          setCurrentStep(pkg.current_step || 1);
          setFormData({
            technology_classification: pkg.technology_classification || formData.technology_classification,
            recovery_strategy: pkg.recovery_strategy || formData.recovery_strategy,
            business_recovery: pkg.business_recovery || formData.business_recovery,
            application_recovery: {
              ...formData.application_recovery,
              ...pkg.application_recovery,
              recovery_tasks: pkg.application_recovery?.recovery_tasks || [],
              steps: pkg.application_recovery?.steps || [],
            },
            data_recovery: pkg.data_recovery || formData.data_recovery,
            dependency_handoffs: pkg.dependency_handoffs || formData.dependency_handoffs,
            infrastructure_support: pkg.infrastructure_support || formData.infrastructure_support,
          });
        } else {
          // Initialize with BIA data
          setFormData(prev => ({
            ...prev,
            recovery_strategy: {
              ...prev.recovery_strategy,
              target_rto_hours: biaResponse.data.bia_summary?.rto_hours || 24,
            },
          }));
        }
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [api, biaId, packageId, navigate]);

  useEffect(() => {
    fetchData();
    // Fetch org users for approver selection
    api.get("/recovery-tasks/org-users-for-approver")
      .then(res => setOrgUsers(res.data.users || []))
      .catch(() => {});
  }, [fetchData, api]);

  // Save progress
  const saveProgress = async (showToast = true) => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        current_step: currentStep,
      };
      
      if (existingPackageId) {
        await api.put(`/recovery-packages/${existingPackageId}`, payload);
      } else {
        const response = await api.post("/recovery-packages/", {
          bia_id: biaId,
          ...payload,
        });
        setExistingPackageId(response.data.id);
      }
      
      if (showToast) {
        toast.success("Progress saved");
      }
    } catch (error) {
      console.error("Failed to save:", error);
      toast.error("Failed to save progress");
    } finally {
      setSaving(false);
    }
  };

  // Submit for review
  const submitForReview = async () => {
    setSubmitting(true);
    try {
      // Save first
      await saveProgress(false);
      
      // Then submit with approver info
      await api.post(`/recovery-packages/${existingPackageId}/submit`, {
        approver_user_id: selectedApprover?.id || null,
        approver_email: selectedApprover?.email || null,
        approver_name: selectedApprover?.name || null,
      });
      toast.success(selectedApprover
        ? `Recovery package submitted — ${selectedApprover.name} will review`
        : "Recovery package submitted for review!"
      );
      navigate("/my-recovery-packages");
    } catch (error) {
      console.error("Failed to submit:", error);
      toast.error(extractErrorMessage(error, "Failed to submit"));
    } finally {
      setSubmitting(false);
    }
  };

  // Navigation
  const goToStep = (step) => {
    if (step >= 1 && step <= 7) {
      saveProgress(false);
      setCurrentStep(step);
    }
  };

  const nextStep = () => {
    if (currentStep < 7) {
      saveProgress(false);
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Add recovery task (DRP-BCP Template format)
  const [newRecoveryTask, setNewRecoveryTask] = useState({
    task_id: "T001",
    actual_task: "",
    task_owner: user?.name || "",
    predecessor_task_id: "",
    estimated_duration: "30 mins",
    estimated_duration_mins: 30,
    notes: "",
  });

  const addRecoveryTask = () => {
    if (!newRecoveryTask.actual_task) {
      toast.error("Please enter the task description");
      return;
    }
    const taskId = newRecoveryTask.task_id || `T${String(formData.application_recovery.recovery_tasks.length + 1).padStart(3, '0')}`;
    setFormData(prev => ({
      ...prev,
      application_recovery: {
        ...prev.application_recovery,
        recovery_tasks: [
          ...prev.application_recovery.recovery_tasks,
          { ...newRecoveryTask, task_id: taskId, start_time: null, completion_time: null, status: "not_started" }
        ],
        steps: [
          ...prev.application_recovery.steps,
          { order: prev.application_recovery.steps.length + 1, action: newRecoveryTask.actual_task, responsible: newRecoveryTask.task_owner, duration_mins: newRecoveryTask.estimated_duration_mins }
        ],
      }
    }));
    setNewRecoveryTask({
      task_id: `T${String(formData.application_recovery.recovery_tasks.length + 2).padStart(3, '0')}`,
      actual_task: "",
      task_owner: user?.name || "",
      predecessor_task_id: taskId,
      estimated_duration: "30 mins",
      estimated_duration_mins: 30,
      notes: "",
    });
  };

  const removeRecoveryTask = (index) => {
    setFormData(prev => ({
      ...prev,
      application_recovery: {
        ...prev.application_recovery,
        recovery_tasks: prev.application_recovery.recovery_tasks.filter((_, i) => i !== index),
        steps: prev.application_recovery.steps.filter((_, i) => i !== index),
      }
    }));
  };

  // Add validation checklist item
  const addValidationItem = () => {
    setFormData(prev => ({
      ...prev,
      application_recovery: {
        ...prev.application_recovery,
        validation_checklist: [...prev.application_recovery.validation_checklist, ""]
      }
    }));
  };

  // Add upstream dependency
  const addUpstreamDependency = () => {
    setFormData(prev => ({
      ...prev,
      dependency_handoffs: {
        ...prev.dependency_handoffs,
        upstream: [
          ...prev.dependency_handoffs.upstream,
          { dependency_name: "", contact_team: "", contact_person: "", notification_method: "email", your_action: "" }
        ]
      }
    }));
  };

  // Add downstream dependency
  const addDownstreamDependency = () => {
    setFormData(prev => ({
      ...prev,
      dependency_handoffs: {
        ...prev.dependency_handoffs,
        downstream: [
          ...prev.dependency_handoffs.downstream,
          { dependency_name: "", contact_team: "", notification_trigger: "", notification_message: "" }
        ]
      }
    }));
  };

  // Get recommended strategy based on RTO
  const getRecommendedStrategy = (rtoHours) => {
    if (rtoHours <= 4) return "hot_standby";
    if (rtoHours <= 24) return "warm_standby";
    if (rtoHours <= 72) return "cold_standby";
    return "manual_workaround";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-sky-700" />
      </div>
    );
  }

  if (!biaSummary) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <AlertTriangle className="w-16 h-16 text-amber-500" />
        <h2 className="text-2xl font-bold text-slate-900">BIA Not Found</h2>
        <p className="text-slate-600">Please complete a BIA before creating a recovery plan.</p>
        <Button onClick={() => navigate("/operational-bia")}>Go to BIA</Button>
      </div>
    );
  }

  const recommendedStrategy = getRecommendedStrategy(biaSummary.rto_hours);

  return (
    <div className="space-y-6" data-testid="recovery-wizard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-emerald-100 text-emerald-700">DO Phase</Badge>
            <Badge variant="outline">ISO 22301 Clause 8.3-8.4</Badge>
            <Badge className="bg-sky-100 text-sky-700">Process Owner</Badge>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Recovery Planning Wizard</h1>
          <p className="text-slate-600 mt-1">
            Create recovery strategy and plan for: <strong>{biaSummary.process_name}</strong>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => saveProgress()} disabled={saving}>
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Draft
          </Button>
        </div>
      </div>

      {/* Wizard Container */}
      <WizardContainer>
        <WizardStepIndicator
          steps={WIZARD_STEPS}
          currentStep={currentStep}
          onStepClick={goToStep}
        />

        <div className="mt-6">
          {/* Step 1: BIA Summary */}
          <WizardStep step={1} currentStep={currentStep}>
            <div className="space-y-6">
              <div className="flex items-center gap-2 mb-4">
                <Info className="w-5 h-5 text-blue-600" />
                <p className="text-sm text-slate-600">
                  This data is auto-populated from your approved BIA. Your recovery strategy must restore operations within the RTO to avoid critical impact.
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-red-600" />
                      <p className="text-xs font-medium text-red-600">RTO (Target)</p>
                    </div>
                    <p className="text-2xl font-bold text-red-700">{biaSummary.rto_hours} hours</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Database className="w-4 h-4 text-amber-600" />
                      <p className="text-xs font-medium text-amber-600">RPO</p>
                    </div>
                    <p className="text-2xl font-bold text-amber-700">{biaSummary.rpo_hours} hours</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Timer className="w-4 h-4 text-blue-600" />
                      <p className="text-xs font-medium text-blue-600">MTPD</p>
                    </div>
                    <p className="text-2xl font-bold text-blue-700">{biaSummary.mtpd_hours} hours</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Target className="w-4 h-4 text-emerald-600" />
                      <p className="text-xs font-medium text-emerald-600">MBCO</p>
                    </div>
                    <p className="text-2xl font-bold text-emerald-700">{biaSummary.mbco_percent}%</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Process Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Process Name:</span>
                      <span className="font-medium">{biaSummary.process_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Department:</span>
                      <span className="font-medium">{biaSummary.department || "N/A"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Process Owner:</span>
                      <span className="font-medium">{biaSummary.process_owner_name || user?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Operational Impact:</span>
                      <Badge className={
                        biaSummary.operational_impact === "high" ? "bg-red-100 text-red-700" :
                        biaSummary.operational_impact === "medium" ? "bg-amber-100 text-amber-700" :
                        "bg-green-100 text-green-700"
                      }>
                        {biaSummary.operational_impact?.toUpperCase() || "MEDIUM"}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Impact Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Financial Impact:</span>
                      <span className="font-medium text-red-600">
                        ${(biaSummary.financial_impact_daily || 0).toLocaleString()}/day
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Upstream Dependencies:</span>
                      <Badge variant="outline">{biaSummary.upstream_dependencies?.length || 0}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Downstream Dependencies:</span>
                      <Badge variant="outline">{biaSummary.downstream_dependencies?.length || 0}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Technology Assets:</span>
                      <Badge variant="outline">{biaSummary.technology_assets?.length || 0}</Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Recovery Requirement</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Based on your RTO of <strong>{biaSummary.rto_hours} hours</strong>, your recovery plan must be able to restore operations within this timeframe. 
                        The recommended strategy is <strong>{RECOVERY_STRATEGIES.find(s => s.id === recommendedStrategy)?.name}</strong>.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </WizardStep>

          {/* Step 2: Technology Classification */}
          <WizardStep step={2} currentStep={currentStep}>
            <div className="space-y-6">
              <div>
                <Label className="text-base font-semibold">Where is your technology hosted?</Label>
                <p className="text-sm text-slate-500 mb-4">This determines who is responsible for infrastructure recovery.</p>
                
                <RadioGroup
                  value={formData.technology_classification.hosting_type}
                  onValueChange={(value) => {
                    const needsIT = ["private_cloud", "on_premise", "hybrid"].includes(value);
                    const needsFacility = ["on_premise", "hybrid"].includes(value);
                    setFormData(prev => ({
                      ...prev,
                      technology_classification: {
                        ...prev.technology_classification,
                        hosting_type: value,
                        devops_managed: ["public_cloud", "hybrid"].includes(value),
                        infrastructure_team_required: needsIT,
                        facility_team_required: needsFacility,
                      }
                    }));
                  }}
                  className="space-y-3"
                >
                  {HOSTING_TYPES.map((type) => (
                    <div
                      key={type.id}
                      className={`flex items-start space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.technology_classification.hosting_type === type.id
                          ? "border-sky-700 bg-sky-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      onClick={() => {
                        const needsIT = ["private_cloud", "on_premise", "hybrid"].includes(type.id);
                        const needsFacility = ["on_premise", "hybrid"].includes(type.id);
                        setFormData(prev => ({
                          ...prev,
                          technology_classification: {
                            ...prev.technology_classification,
                            hosting_type: type.id,
                            devops_managed: ["public_cloud", "hybrid"].includes(type.id),
                            infrastructure_team_required: needsIT,
                            facility_team_required: needsFacility,
                          }
                        }));
                      }}
                    >
                      <RadioGroupItem value={type.id} id={type.id} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <type.icon className="w-5 h-5 text-slate-600" />
                          <Label htmlFor={type.id} className="font-medium cursor-pointer">{type.name}</Label>
                        </div>
                        <p className="text-sm text-slate-500 mt-1 ml-7">{type.description}</p>
                      </div>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {formData.technology_classification.hosting_type === "public_cloud" && (
                <div>
                  <Label>Cloud Provider</Label>
                  <Select
                    value={formData.technology_classification.cloud_provider}
                    onValueChange={(value) => setFormData(prev => ({
                      ...prev,
                      technology_classification: { ...prev.technology_classification, cloud_provider: value }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select cloud provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AWS">Amazon Web Services (AWS)</SelectItem>
                      <SelectItem value="Azure">Microsoft Azure</SelectItem>
                      <SelectItem value="GCP">Google Cloud Platform</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Team Requirements Summary */}
              {formData.technology_classification.hosting_type && (
                <Card className="bg-slate-50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Recovery Responsibilities</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Checkbox checked={true} disabled />
                        <span className="text-sm"><strong>You (Process Owner)</strong>: Business & Application Recovery</span>
                      </div>
                      {formData.technology_classification.devops_managed && (
                        <div className="flex items-center gap-2">
                          <Checkbox checked={true} disabled />
                          <span className="text-sm"><strong>You (DevOps)</strong>: Infrastructure as Code, Failover</span>
                        </div>
                      )}
                      {formData.technology_classification.infrastructure_team_required && (
                        <div className="flex items-center gap-2">
                          <Checkbox checked={true} disabled />
                          <span className="text-sm"><strong>IT Team</strong>: Server, Network, Storage Recovery</span>
                        </div>
                      )}
                      {formData.technology_classification.facility_team_required && (
                        <div className="flex items-center gap-2">
                          <Checkbox checked={true} disabled />
                          <span className="text-sm"><strong>Facility Team</strong>: Physical Access, Power, Cooling</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </WizardStep>

          {/* Step 3: Recovery Strategy */}
          <WizardStep step={3} currentStep={currentStep}>
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-semibold">Select Recovery Strategy</Label>
                  <p className="text-sm text-slate-500">Based on your RTO of <strong>{biaSummary.rto_hours} hours</strong></p>
                </div>
                <Badge className="bg-sky-100 text-sky-700">
                  Recommended: {RECOVERY_STRATEGIES.find(s => s.id === recommendedStrategy)?.name}
                </Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {RECOVERY_STRATEGIES.map((strategy) => (
                  <Card
                    key={strategy.id}
                    className={`cursor-pointer transition-all ${
                      formData.recovery_strategy.strategy_type === strategy.id
                        ? "border-2 border-sky-700 shadow-md"
                        : "border hover:border-slate-300"
                    } ${strategy.id === recommendedStrategy ? "ring-2 ring-indigo-200" : ""}`}
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      recovery_strategy: {
                        ...prev.recovery_strategy,
                        strategy_type: strategy.id,
                        cost_estimate: strategy.cost.toLowerCase(),
                      }
                    }))}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`p-2 rounded-lg ${strategy.color}`}>
                            <strategy.icon className="w-5 h-5" />
                          </div>
                          <div>
                            <h3 className="font-semibold">{strategy.name}</h3>
                            <p className="text-xs text-slate-500">RTO: {strategy.rtoRange}</p>
                          </div>
                        </div>
                        {strategy.id === recommendedStrategy && (
                          <Badge className="bg-sky-100 text-sky-700 text-xs">Recommended</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mb-3">{strategy.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex gap-1">
                          {strategy.features.slice(0, 2).map((f, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{f}</Badge>
                          ))}
                        </div>
                        <Badge className={
                          strategy.cost === "High" ? "bg-red-100 text-red-700" :
                          strategy.cost === "Medium" ? "bg-amber-100 text-amber-700" :
                          "bg-green-100 text-green-700"
                        }>
                          Cost: {strategy.cost}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {formData.recovery_strategy.strategy_type && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Planned RTO (hours)</Label>
                    <Input
                      type="number"
                      value={formData.recovery_strategy.planned_rto_hours}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        recovery_strategy: { ...prev.recovery_strategy, planned_rto_hours: parseFloat(e.target.value) || 0 }
                      }))}
                      placeholder="Your planned recovery time"
                    />
                    {formData.recovery_strategy.planned_rto_hours > biaSummary.rto_hours && (
                      <p className="text-xs text-red-600 mt-1">
                        ⚠️ Planned RTO exceeds target RTO of {biaSummary.rto_hours} hours
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Strategy Justification</Label>
                    <Textarea
                      value={formData.recovery_strategy.justification}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        recovery_strategy: { ...prev.recovery_strategy, justification: e.target.value }
                      }))}
                      placeholder="Why did you choose this strategy?"
                      rows={2}
                    />
                  </div>
                </div>
              )}
            </div>
          </WizardStep>

          {/* Step 4: Recovery Procedures */}
          <WizardStep step={4} currentStep={currentStep}>
            <div className="space-y-6">
              {/* Business Recovery */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    Business Continuity (Manual Workarounds)
                  </CardTitle>
                  <CardDescription>What can your team do without technology?</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Manual Workaround Procedures</Label>
                    <Textarea
                      value={formData.business_recovery.manual_workarounds}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        business_recovery: { ...prev.business_recovery, manual_workarounds: e.target.value }
                      }))}
                      placeholder="Describe how your team can continue operations manually..."
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Workaround Duration (hours)</Label>
                      <Input
                        type="number"
                        value={formData.business_recovery.workaround_duration_hours}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          business_recovery: { ...prev.business_recovery, workaround_duration_hours: parseInt(e.target.value) || 0 }
                        }))}
                      />
                    </div>
                    <div>
                      <Label>Minimum Staff Required</Label>
                      <Input
                        type="number"
                        value={formData.business_recovery.minimum_staff_required}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          business_recovery: { ...prev.business_recovery, minimum_staff_required: parseInt(e.target.value) || 1 }
                        }))}
                      />
                    </div>
                    <div>
                      <Label>Alternate Location</Label>
                      <Input
                        value={formData.business_recovery.alternate_location}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          business_recovery: { ...prev.business_recovery, alternate_location: e.target.value }
                        }))}
                        placeholder="e.g., Work from home, Branch office"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Application Recovery */}
              {(formData.technology_classification.devops_managed || formData.technology_classification.hosting_type === "public_cloud") && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Server className="w-5 h-5 text-emerald-600" />
                      Application Recovery (DevOps)
                    </CardTitle>
                    <CardDescription>Define step-by-step recovery tasks using the standard DRP/BCP template. Start Time and Completion Time are recorded during exercises.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <RecoveryTaskTable
                      tasks={formData.application_recovery.recovery_tasks}
                      onTasksChange={(updated) => {
                        const legacySteps = updated.map((t, i) => ({
                          order: i + 1,
                          action: t.actual_task,
                          responsible: t.task_owner,
                          duration_mins: t.estimated_duration_mins || 30,
                        }));
                        setFormData(prev => ({
                          ...prev,
                          application_recovery: {
                            ...prev.application_recovery,
                            recovery_tasks: updated,
                            steps: legacySteps,
                          }
                        }));
                      }}
                      userName={user?.name || ""}
                      emptyMessage="No recovery tasks defined yet. Click 'Add Task' to begin."
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Failover Procedure</Label>
                        <Textarea
                          value={formData.application_recovery.failover_procedure}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            application_recovery: { ...prev.application_recovery, failover_procedure: e.target.value }
                          }))}
                          placeholder="How to initiate failover..."
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label>Data Restoration Procedure</Label>
                        <Textarea
                          value={formData.application_recovery.data_restoration_procedure}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            application_recovery: { ...prev.application_recovery, data_restoration_procedure: e.target.value }
                          }))}
                          placeholder="How to restore data..."
                          rows={2}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Data Recovery */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Database className="w-5 h-5 text-purple-600" />
                    Data Recovery
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label>Backup Location</Label>
                      <Input
                        value={formData.data_recovery.backup_location}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          data_recovery: { ...prev.data_recovery, backup_location: e.target.value }
                        }))}
                        placeholder="e.g., S3, Azure Blob"
                      />
                    </div>
                    <div>
                      <Label>Backup Frequency</Label>
                      <Select
                        value={formData.data_recovery.backup_frequency}
                        onValueChange={(value) => setFormData(prev => ({
                          ...prev,
                          data_recovery: { ...prev.data_recovery, backup_frequency: value }
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="realtime">Real-time</SelectItem>
                          <SelectItem value="hourly">Hourly</SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Retention (days)</Label>
                      <Input
                        type="number"
                        value={formData.data_recovery.backup_retention_days}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          data_recovery: { ...prev.data_recovery, backup_retention_days: parseInt(e.target.value) || 30 }
                        }))}
                      />
                    </div>
                    <div>
                      <Label>Est. Restore Time (hrs)</Label>
                      <Input
                        type="number"
                        value={formData.data_recovery.estimated_restore_time_hours}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          data_recovery: { ...prev.data_recovery, estimated_restore_time_hours: parseFloat(e.target.value) || 0 }
                        }))}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </WizardStep>

          {/* Step 5: Dependencies */}
          <WizardStep step={5} currentStep={currentStep}>
            <div className="space-y-6">
              {/* Upstream Dependencies */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ArrowLeft className="w-5 h-5 text-blue-600" />
                    Upstream Dependencies (You depend on these)
                  </CardTitle>
                  <CardDescription>Who do you need to wait for before you can recover?</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {formData.dependency_handoffs.upstream.map((dep, idx) => (
                      <div key={idx} className="grid grid-cols-5 gap-2 items-end">
                        <div>
                          <Label className="text-xs">Dependency</Label>
                          <Input
                            value={dep.dependency_name}
                            onChange={(e) => {
                              const newDeps = [...formData.dependency_handoffs.upstream];
                              newDeps[idx].dependency_name = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                dependency_handoffs: { ...prev.dependency_handoffs, upstream: newDeps }
                              }));
                            }}
                            placeholder="e.g., IT Infrastructure"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Contact Team</Label>
                          <Input
                            value={dep.contact_team}
                            onChange={(e) => {
                              const newDeps = [...formData.dependency_handoffs.upstream];
                              newDeps[idx].contact_team = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                dependency_handoffs: { ...prev.dependency_handoffs, upstream: newDeps }
                              }));
                            }}
                            placeholder="e.g., IT Team"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Contact Person</Label>
                          <Input
                            value={dep.contact_person}
                            onChange={(e) => {
                              const newDeps = [...formData.dependency_handoffs.upstream];
                              newDeps[idx].contact_person = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                dependency_handoffs: { ...prev.dependency_handoffs, upstream: newDeps }
                              }));
                            }}
                            placeholder="Name"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Your Action</Label>
                          <Input
                            value={dep.your_action}
                            onChange={(e) => {
                              const newDeps = [...formData.dependency_handoffs.upstream];
                              newDeps[idx].your_action = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                dependency_handoffs: { ...prev.dependency_handoffs, upstream: newDeps }
                              }));
                            }}
                            placeholder="What you do after they recover"
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newDeps = formData.dependency_handoffs.upstream.filter((_, i) => i !== idx);
                            setFormData(prev => ({
                              ...prev,
                              dependency_handoffs: { ...prev.dependency_handoffs, upstream: newDeps }
                            }));
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={addUpstreamDependency}>
                      <Plus className="w-4 h-4 mr-1" /> Add Upstream Dependency
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Downstream Dependencies */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-600" />
                    Downstream Dependencies (These depend on you)
                  </CardTitle>
                  <CardDescription>Who should you notify when you're recovered?</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {formData.dependency_handoffs.downstream.map((dep, idx) => (
                      <div key={idx} className="grid grid-cols-4 gap-2 items-end">
                        <div>
                          <Label className="text-xs">Dependency</Label>
                          <Input
                            value={dep.dependency_name}
                            onChange={(e) => {
                              const newDeps = [...formData.dependency_handoffs.downstream];
                              newDeps[idx].dependency_name = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                dependency_handoffs: { ...prev.dependency_handoffs, downstream: newDeps }
                              }));
                            }}
                            placeholder="e.g., Customer Portal"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Contact Team</Label>
                          <Input
                            value={dep.contact_team}
                            onChange={(e) => {
                              const newDeps = [...formData.dependency_handoffs.downstream];
                              newDeps[idx].contact_team = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                dependency_handoffs: { ...prev.dependency_handoffs, downstream: newDeps }
                              }));
                            }}
                            placeholder="Team name"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Notification Message</Label>
                          <Input
                            value={dep.notification_message}
                            onChange={(e) => {
                              const newDeps = [...formData.dependency_handoffs.downstream];
                              newDeps[idx].notification_message = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                dependency_handoffs: { ...prev.dependency_handoffs, downstream: newDeps }
                              }));
                            }}
                            placeholder="Message to send when recovered"
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newDeps = formData.dependency_handoffs.downstream.filter((_, i) => i !== idx);
                            setFormData(prev => ({
                              ...prev,
                              dependency_handoffs: { ...prev.dependency_handoffs, downstream: newDeps }
                            }));
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={addDownstreamDependency}>
                      <Plus className="w-4 h-4 mr-1" /> Add Downstream Dependency
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </WizardStep>

          {/* Step 6: Infrastructure Support */}
          <WizardStep step={6} currentStep={currentStep}>
            <div className="space-y-6">
              {!formData.technology_classification.infrastructure_team_required && 
               !formData.technology_classification.facility_team_required ? (
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                      <div>
                        <p className="font-medium text-emerald-800">No Infrastructure Support Required</p>
                        <p className="text-sm text-emerald-700">
                          Based on your technology classification ({HOSTING_TYPES.find(t => t.id === formData.technology_classification.hosting_type)?.name}), 
                          you manage your own infrastructure recovery. Proceed to the next step.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-blue-800">Infrastructure Support Required</p>
                          <p className="text-sm text-blue-700">
                            Based on your technology classification, the following teams will be notified to acknowledge your recovery requirements.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* IT Team Requirements */}
                  {formData.technology_classification.infrastructure_team_required && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Server className="w-5 h-5 text-blue-600" />
                          IT Team - Infrastructure Recovery
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Server Requirements</Label>
                            <Textarea
                              value={formData.infrastructure_support.it_requirements?.servers?.join(", ") || ""}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                infrastructure_support: {
                                  ...prev.infrastructure_support,
                                  it_team_required: true,
                                  it_requirements: {
                                    ...prev.infrastructure_support.it_requirements,
                                    servers: e.target.value.split(",").map(s => s.trim()).filter(s => s)
                                  }
                                }
                              }))}
                              placeholder="List servers (comma-separated)"
                              rows={2}
                            />
                          </div>
                          <div>
                            <Label>Storage Required (GB)</Label>
                            <Input
                              type="number"
                              value={formData.infrastructure_support.it_requirements?.storage_gb || ""}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                infrastructure_support: {
                                  ...prev.infrastructure_support,
                                  it_team_required: true,
                                  it_requirements: {
                                    ...prev.infrastructure_support.it_requirements,
                                    storage_gb: parseInt(e.target.value) || 0
                                  }
                                }
                              }))}
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Network Requirements</Label>
                          <Textarea
                            value={formData.infrastructure_support.it_requirements?.network_requirements || ""}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              infrastructure_support: {
                                ...prev.infrastructure_support,
                                it_team_required: true,
                                it_requirements: {
                                  ...prev.infrastructure_support.it_requirements,
                                  network_requirements: e.target.value
                                }
                              }
                            }))}
                            placeholder="VPN, firewall rules, bandwidth requirements..."
                            rows={2}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Facility Team Requirements */}
                  {formData.technology_classification.facility_team_required && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Building2 className="w-5 h-5 text-amber-600" />
                          Facility Team - Site Recovery
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Location</Label>
                            <Input
                              value={formData.infrastructure_support.facility_requirements?.location || ""}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                infrastructure_support: {
                                  ...prev.infrastructure_support,
                                  facility_team_required: true,
                                  facility_requirements: {
                                    ...prev.infrastructure_support.facility_requirements,
                                    location: e.target.value
                                  }
                                }
                              }))}
                              placeholder="Building, Floor, Room"
                            />
                          </div>
                          <div>
                            <Label>Physical Access Requirements</Label>
                            <Input
                              value={formData.infrastructure_support.facility_requirements?.physical_access || ""}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                infrastructure_support: {
                                  ...prev.infrastructure_support,
                                  facility_team_required: true,
                                  facility_requirements: {
                                    ...prev.infrastructure_support.facility_requirements,
                                    physical_access: e.target.value
                                  }
                                }
                              }))}
                              placeholder="e.g., 24/7 access required"
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Power Requirements</Label>
                          <Input
                            value={formData.infrastructure_support.facility_requirements?.power_requirements || ""}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              infrastructure_support: {
                                ...prev.infrastructure_support,
                                facility_team_required: true,
                                facility_requirements: {
                                  ...prev.infrastructure_support.facility_requirements,
                                  power_requirements: e.target.value
                                }
                              }
                            }))}
                            placeholder="UPS, generator, power capacity..."
                          />
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </div>
          </WizardStep>

          {/* Step 7: Review & Submit */}
          <WizardStep step={7} currentStep={currentStep}>
            <div className="space-y-6">
              <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-sky-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle2 className="w-6 h-6 text-sky-700" />
                    Recovery Package Summary
                  </CardTitle>
                  <CardDescription>Review your recovery package before submitting for approval</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-slate-500">Process</p>
                        <p className="font-medium">{biaSummary.process_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Strategy</p>
                        <Badge className={RECOVERY_STRATEGIES.find(s => s.id === formData.recovery_strategy.strategy_type)?.color || ""}>
                          {RECOVERY_STRATEGIES.find(s => s.id === formData.recovery_strategy.strategy_type)?.name || "Not selected"}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Hosting</p>
                        <p className="font-medium">
                          {HOSTING_TYPES.find(t => t.id === formData.technology_classification.hosting_type)?.name || "Not specified"}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-500">Target RTO:</span>
                        <span className="font-medium">{biaSummary.rto_hours} hours</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-500">Planned RTO:</span>
                        <span className={`font-medium ${formData.recovery_strategy.planned_rto_hours > biaSummary.rto_hours ? "text-red-600" : "text-emerald-600"}`}>
                          {formData.recovery_strategy.planned_rto_hours || "N/A"} hours
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-500">Recovery Tasks:</span>
                        <span className="font-medium">{formData.application_recovery.recovery_tasks.length || formData.application_recovery.steps.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-500">Dependencies:</span>
                        <span className="font-medium">
                          {formData.dependency_handoffs.upstream.length} upstream, {formData.dependency_handoffs.downstream.length} downstream
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Completion Checklist */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Completion Checklist</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Checkbox checked={true} disabled />
                      <span className="text-sm">✅ BIA Summary Reviewed</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox checked={!!formData.technology_classification.hosting_type} disabled />
                      <span className="text-sm">{formData.technology_classification.hosting_type ? "✅" : "⏳"} Technology Classified</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox checked={!!formData.recovery_strategy.strategy_type} disabled />
                      <span className="text-sm">{formData.recovery_strategy.strategy_type ? "✅" : "⏳"} Recovery Strategy Selected</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox checked={!!formData.business_recovery.manual_workarounds} disabled />
                      <span className="text-sm">{formData.business_recovery.manual_workarounds ? "✅" : "⏳"} Business Recovery Procedures</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox checked={formData.dependency_handoffs.upstream.length > 0 || formData.dependency_handoffs.downstream.length > 0} disabled />
                      <span className="text-sm">{(formData.dependency_handoffs.upstream.length > 0 || formData.dependency_handoffs.downstream.length > 0) ? "✅" : "⏳"} Dependencies Mapped</span>
                    </div>
                    {formData.technology_classification.infrastructure_team_required && (
                      <div className="flex items-center gap-2">
                        <Checkbox checked={true} disabled />
                        <span className="text-sm">⏳ IT Team Acknowledgment (Pending)</span>
                      </div>
                    )}
                    {formData.technology_classification.facility_team_required && (
                      <div className="flex items-center gap-2">
                        <Checkbox checked={true} disabled />
                        <span className="text-sm">⏳ Facility Team Acknowledgment (Pending)</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Approver Selection */}
              <Card className="border-sky-200 bg-sky-50/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-indigo-800">
                    <ClipboardCheck className="w-5 h-5" />
                    Designate Your Approver / Manager
                  </CardTitle>
                  <CardDescription className="text-sky-700">
                    Select the person who will review and approve this recovery package. This should be your functional manager or department head.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Select
                    value={selectedApprover?.id || "none"}
                    onValueChange={(val) => {
                      if (val === "none") {
                        setSelectedApprover(null);
                      } else {
                        const u = orgUsers.find(u => u.id === val);
                        setSelectedApprover(u || null);
                      }
                    }}
                  >
                    <SelectTrigger data-testid="approver-select">
                      <SelectValue placeholder="Select an approver..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No specific approver</SelectItem>
                      {orgUsers.filter(u => u.id !== user?.id).map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name} ({u.email}){u.department ? ` — ${u.department}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedApprover && (
                    <div className="flex items-center gap-2 text-sm text-sky-700 bg-sky-100 p-2 rounded mt-3">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Approver: <strong>{selectedApprover.name}</strong> ({selectedApprover.email})</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Submit Button */}
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => saveProgress()}>
                  <Save className="w-4 h-4 mr-2" />
                  Save as Draft
                </Button>
                <Button 
                  onClick={submitForReview} 
                  disabled={submitting || !formData.technology_classification.hosting_type || !formData.recovery_strategy.strategy_type}
                  className="bg-sky-700 hover:bg-sky-800"
                >
                  {submitting ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  {selectedApprover ? `Submit to ${selectedApprover.name}` : "Submit for Approval"}
                </Button>
              </div>
            </div>
          </WizardStep>
        </div>

        {/* Navigation */}
        <WizardNavigation
          currentStep={currentStep}
          totalSteps={7}
          onPrevious={prevStep}
          onNext={nextStep}
          canProceed={true}
        />
      </WizardContainer>
    </div>
  );
}
